import React, { useState, useEffect } from 'react';
import { auth } from '../../firebase.js';
import '../../css/TaskReference.css';

const PRIORITY_COLORS = {
  URGENT: '#e55b5b',
  HIGH: '#f5a623',
  MEDIUM: '#5B92E5',
  LOW: '#7ED321',
  NONE: null,
};

const PRIORITY_LABELS = {
  URGENT: 'Urgent',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
  NONE: null,
};

function TaskDetailModal({ boardTaskId, boardId, taskDbId, onClose }) {
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleOpenBoard = () => {
    onClose();
    window.location.href = `https://tasks.bluebird-documentation.com/board/${boardId}`;
  };

  useEffect(() => {
    const fetchTaskDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const user = auth.currentUser;
        if (!user) {
          setError('Not authenticated');
          setLoading(false);
          return;
        }

        const token = await user.getIdToken();
        const response = await fetch(
          `https://bluebirddocumentationadmin.pythonanywhere.com/tasks/boards/${boardId}/tasks/${taskDbId}/`,
          {
            method: 'GET',
            headers: {
              'Authorization': `${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch task details: ${response.status}`);
        }

        const data = await response.json();
        setTask(data);
      } catch (err) {
        console.error('Error fetching task details:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (boardTaskId && boardId && taskDbId) {
      fetchTaskDetails();
    }
  }, [boardTaskId, boardId, taskDbId]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Close on backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (loading) {
    return (
      <div className="TaskReferenceDetailModal__overlay" onClick={handleBackdropClick}>
        <div className="TaskReferenceDetailModal__dialog">
          <div className="TaskReferenceDetailModal__loading">
            <div className="TaskReferenceDetailModal__spinner"></div>
            <p>Loading task details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="TaskReferenceDetailModal__overlay" onClick={handleBackdropClick}>
        <div className="TaskReferenceDetailModal__dialog">
          <div className="TaskReferenceDetailModal__error">
            <i className="fas fa-exclamation-circle"></i>
            <p>{error || 'Failed to load task details'}</p>
          </div>
        </div>
      </div>
    );
  }

  const priorityColor = PRIORITY_COLORS[task.priority];
  const priorityLabel = PRIORITY_LABELS[task.priority];

  return (
    <div className="TaskReferenceDetailModal__overlay" onClick={handleBackdropClick}>
      <div className="TaskReferenceDetailModal__dialog">
        {/* Header */}
        <div className="TaskReferenceDetailModal__header">
          <span className="TaskReferenceDetailModal__task-id-badge">{boardTaskId}</span>
          <button
            className="TaskReferenceDetailModal__close-btn"
            onClick={onClose}
            title="Close modal"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Title */}
        <div className="TaskReferenceDetailModal__title">
          {task.title}
        </div>

        {/* Action Buttons */}
        <div className="TaskReferenceDetailModal__actions">
          <button
            className="TaskReferenceDetailModal__view-board-btn"
            onClick={handleOpenBoard}
            title="Open board"
          >
            <i className="fas fa-arrow-right"></i>
            View Board
          </button>
        </div>

        {/* Content Sections */}
        <div className="TaskReferenceDetailModal__content">
          {/* Priority Section */}
          {task.priority && task.priority !== 'NONE' && (
            <div className="TaskReferenceDetailModal__section">
              <span className="TaskReferenceDetailModal__label">Priority</span>
              <span
                className={`TaskReferenceDetailModal__priority-badge TaskReferenceDetailModal__priority-badge--${task.priority.toLowerCase()}`}
                style={{
                  color: priorityColor,
                  borderColor: priorityColor,
                }}
              >
                {priorityLabel}
              </span>
            </div>
          )}

          {/* Column Section */}
          {task.column_name && (
            <div className="TaskReferenceDetailModal__section">
              <span className="TaskReferenceDetailModal__label">Column</span>
              <span className="TaskReferenceDetailModal__value">{task.column_name}</span>
            </div>
          )}

          {/* Board Section */}
          {task.board_name && (
            <div className="TaskReferenceDetailModal__section">
              <span className="TaskReferenceDetailModal__label">Board</span>
              <span className="TaskReferenceDetailModal__value">{task.board_name}</span>
            </div>
          )}

          {/* Due Date Section */}
          {task.due_date && (
            <div className="TaskReferenceDetailModal__section">
              <span className="TaskReferenceDetailModal__label">Due Date</span>
              <span className="TaskReferenceDetailModal__value">
                {new Date(task.due_date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
            </div>
          )}

          {/* Labels Section */}
          {task.labels && task.labels.length > 0 && (
            <div className="TaskReferenceDetailModal__section">
              <span className="TaskReferenceDetailModal__label">Labels</span>
              <div className="TaskReferenceDetailModal__labels-container">
                {task.labels.map((label, idx) => (
                  <span
                    key={idx}
                    className="TaskReferenceDetailModal__label-chip"
                    style={{ backgroundColor: label.color || '#5B92E5' }}
                  >
                    {label.name || label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Description Section */}
          {task.description && (
            <div className="TaskReferenceDetailModal__section">
              <span className="TaskReferenceDetailModal__label">Description</span>
              <p className="TaskReferenceDetailModal__description">
                {task.description}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TaskDetailModal;
